import React, { useCallback, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  FlatList,
  Pressable,
  RefreshControl,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import { AppCard } from '../../components/AppCard';
import { AppDateField } from '../../components/AppDateField';
import { AppInput } from '../../components/AppInput';
import { AppScreen } from '../../components/AppScreen';
import { EmptyState } from '../../components/EmptyState';
import { ErrorState } from '../../components/ErrorState';
import { PermissionGuard } from '../../components/PermissionGuard';
import { ScreenHeader } from '../../components/ScreenHeader';
import { StatusBadge } from '../../components/StatusBadge';
import { PERMISSIONS } from '../../constants/permissions';
import type { RootStackParamList } from '../../constants/routes';
import { spacing } from '../../constants/spacing';
import { typography } from '../../constants/typography';
import { useAppColors } from '../../context/PreferencesContext';
import { useVisitorHistory } from '../../hooks/useVisitors';
import { VISIT_STATUS, type Visit, type VisitStatus } from '../../types/visitor';
import {
  formatCalendarDate,
  formatTime,
  todayBusinessCalendarDate,
} from '../../utils/dateTime';
import { UnauthorizedScreen } from '../system/UnauthorizedScreen';

type Props = NativeStackScreenProps<RootStackParamList, 'VisitorHistory'>;

const STATUS_OPTIONS: Array<{ label: string; value: VisitStatus | null }> = [
  { label: 'All', value: null },
  { label: 'Inside', value: VISIT_STATUS.INSIDE },
  { label: 'Checked out', value: VISIT_STATUS.CHECKED_OUT },
];

function visitStatusLabel(status: VisitStatus): string {
  return status === VISIT_STATUS.INSIDE ? 'Inside' : 'Checked out';
}

export function VisitorHistoryScreen({ navigation }: Props) {
  const palette = useAppColors();
  const [visitDate, setVisitDate] = useState(() => todayBusinessCalendarDate());
  const [status, setStatus] = useState<VisitStatus | null>(null);
  const list = useVisitorHistory({ visitDate, status });

  const themed = useMemo(
    () => ({
      chip: {
        borderWidth: 1,
        borderColor: palette.border,
        borderRadius: 999,
        paddingHorizontal: spacing.md,
        paddingVertical: spacing.xs,
        backgroundColor: palette.surface,
      },
      chipSelected: { borderColor: palette.primary },
      chipText: { ...typography.caption, color: palette.textPrimary },
      link: { ...typography.caption, color: palette.info, fontWeight: '600' as const },
      name: { ...typography.body, color: palette.textPrimary, fontWeight: '600' as const },
      meta: { ...typography.caption, color: palette.textSecondary },
      label: { ...typography.caption, color: palette.textMuted },
      value: { ...typography.body, color: palette.textPrimary },
    }),
    [palette],
  );

  const apply = useCallback(
    (next: { visitDate: string; status: VisitStatus | null }) => {
      setVisitDate(next.visitDate);
      setStatus(next.status);
      list.applyFilters({ visitDate: next.visitDate, status: next.status });
    },
    [list],
  );

  const showCurrentlyInside = useCallback(
    () => apply({ visitDate: '', status: VISIT_STATUS.INSIDE }),
    [apply],
  );

  const hasFilters = Boolean(list.search.trim()) || status !== null;
  const scope = visitDate ? `on ${formatCalendarDate(visitDate)}` : 'for any date';

  const renderVisit = ({ item }: { item: Visit }) => (
    <AppCard style={styles.card}>
      <View style={styles.cardHeader}>
        <View style={styles.cardTitle}>
          <Text style={themed.name}>{item.visitorName}</Text>
          <Text style={themed.meta}>
            {[item.designation, item.companyName].filter(Boolean).join(' · ')}
          </Text>
        </View>
        <StatusBadge label={visitStatusLabel(item.visitStatus)} />
      </View>
      {item.purposeOfVisit ? (
        <Text style={themed.meta}>Purpose: {item.purposeOfVisit}</Text>
      ) : null}
      <View style={styles.times}>
        <View style={styles.time}>
          <Text style={themed.label}>Visit date</Text>
          <Text style={themed.value}>{formatCalendarDate(item.visitDate)}</Text>
        </View>
        <View style={styles.time}>
          <Text style={themed.label}>Check-in</Text>
          <Text style={themed.value}>{formatTime(item.checkInAt)}</Text>
        </View>
        <View style={styles.time}>
          <Text style={themed.label}>Check-out</Text>
          <Text style={themed.value}>
            {item.checkOutAt ? formatTime(item.checkOutAt) : '—'}
          </Text>
        </View>
      </View>
    </AppCard>
  );

  return (
    <PermissionGuard
      permission={PERMISSIONS.VISITOR_VIEW}
      fallback={<UnauthorizedScreen />}
    >
      <AppScreen contentStyle={styles.screen}>
        <ScreenHeader title="Visitor history" onBack={navigation.goBack} />
        <View style={styles.body}>
          <AppDateField
            label="Visit date"
            value={visitDate}
            onChange={value => apply({ visitDate: value, status })}
            placeholder="All dates"
            maximumDate={new Date()}
          />
          <View style={styles.row}>
            {visitDate ? (
              <Pressable
                accessibilityRole="button"
                onPress={() => apply({ visitDate: '', status })}
              >
                <Text style={themed.link}>Show all dates</Text>
              </Pressable>
            ) : (
              <Pressable
                accessibilityRole="button"
                onPress={() => apply({ visitDate: todayBusinessCalendarDate(), status })}
              >
                <Text style={themed.link}>Show today</Text>
              </Pressable>
            )}
            <Pressable accessibilityRole="button" onPress={showCurrentlyInside}>
              <Text style={themed.link}>Currently inside</Text>
            </Pressable>
          </View>

          <View style={styles.row}>
            {STATUS_OPTIONS.map(option => {
              const selected = status === option.value;
              return (
                <Pressable
                  key={option.label}
                  accessibilityRole="button"
                  accessibilityState={{ selected }}
                  accessibilityLabel={`Show ${option.label.toLowerCase()} visits`}
                  onPress={() => apply({ visitDate, status: option.value })}
                  style={[themed.chip, selected && themed.chipSelected]}
                >
                  <Text style={themed.chipText}>{option.label}</Text>
                </Pressable>
              );
            })}
          </View>

          <AppInput
            label="Search"
            value={list.search}
            onChangeText={list.setSearch}
            placeholder="Visitor name or company"
            autoCapitalize="none"
          />

          {list.isLoading && list.items.length === 0 ? (
            <View style={styles.center}>
              <ActivityIndicator
                color={palette.primary}
                accessibilityLabel="Loading visitor history"
              />
            </View>
          ) : list.error && list.items.length === 0 ? (
            <ErrorState message={list.error} onRetry={list.retry} />
          ) : (
            <FlatList
              data={list.items}
              keyExtractor={item => String(item.visitId)}
              refreshControl={
                <RefreshControl refreshing={list.isRefreshing} onRefresh={list.refresh} />
              }
              onEndReached={list.loadMore}
              onEndReachedThreshold={0.4}
              ListFooterComponent={
                list.isLoadingMore ? (
                  <View style={styles.footer}>
                    <ActivityIndicator color={palette.primary} />
                  </View>
                ) : null
              }
              contentContainerStyle={styles.listContent}
              ListEmptyComponent={
                <EmptyState
                  title={`No visits ${scope}.`}
                  message={
                    hasFilters
                      ? 'Try clearing the search or status filter.'
                      : 'Visitor check-ins will appear here.'
                  }
                  actionTitle={hasFilters ? 'Clear filters' : undefined}
                  onAction={
                    hasFilters
                      ? () => {
                          list.setSearch('');
                          apply({ visitDate, status: null });
                        }
                      : undefined
                  }
                />
              }
              renderItem={renderVisit}
            />
          )}
        </View>
      </AppScreen>
    </PermissionGuard>
  );
}

const styles = StyleSheet.create({
  screen: { flex: 1, paddingBottom: 0 },
  body: { flex: 1, marginTop: spacing.lg, gap: spacing.sm },
  row: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.md, alignItems: 'center' },
  listContent: { flexGrow: 1, paddingBottom: spacing.xl, gap: spacing.sm },
  card: { gap: spacing.sm },
  cardHeader: { flexDirection: 'row', alignItems: 'flex-start', gap: spacing.sm },
  cardTitle: { flex: 1, gap: spacing.xs },
  times: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.lg },
  time: { gap: 2, minWidth: 90 },
  footer: { paddingVertical: spacing.lg, alignItems: 'center' },
  center: { flex: 1, alignItems: 'center', justifyContent: 'center' },
});
